import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { testimonials } from "@/config/site";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const total = testimonials.length;
  const current = testimonials[idx];

  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div className="vs-container relative z-10">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="text-overline">In their words</span>
            </div>
            <h2 className="mt-5 font-serif-display text-4xl md:text-6xl text-white tracking-tight leading-[1.02] max-w-2xl">
              Trusted by owners
              <br />
              who <span className="italic vs-gold-text">think in decades.</span>
            </h2>
          </motion.div>
          <div
            data-testid="testimonials-theme-label"
            className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body"
          >
            Across P&amp;L · Cash Flow · Balance Sheet · Modelling
          </div>
        </div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="vs-glass rounded-sm p-8 md:p-16 relative"
              data-testid={`testimonial-${idx}`}
            >
              <Quote
                className="absolute top-6 left-6 md:top-10 md:left-10 text-[#d4af37]/50"
                size={44}
              />
              <p className="pl-14 md:pl-20 font-serif-display italic text-2xl md:text-4xl text-white/90 leading-snug tracking-tight max-w-4xl">
                “{current.quote}”
              </p>
              <div className="mt-10 pl-14 md:pl-20 flex flex-wrap items-center justify-between gap-6 border-t border-white/10 pt-6">
                <div>
                  <div className="font-serif-display text-xl text-white">
                    {current.name}
                  </div>
                  <div className="text-xs text-white/60 font-body tracking-wide mt-1">
                    {current.title} · <span className="text-[#d4af37]/80">{current.company}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="testimonial-prev"
                    onClick={prev}
                    aria-label="Previous testimonial"
                    className="h-10 w-10 rounded-full border border-white/15 hover:border-[#d4af37] flex items-center justify-center text-white/70 hover:text-[#d4af37] transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="font-mono-accent text-xs text-white/40">
                    {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                  </span>
                  <button
                    data-testid="testimonial-next"
                    onClick={next}
                    aria-label="Next testimonial"
                    className="h-10 w-10 rounded-full border border-white/15 hover:border-[#d4af37] flex items-center justify-center text-white/70 hover:text-[#d4af37] transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </motion.blockquote>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
